<script setup lang="ts">
import { computed } from "vue";
import Tag from "./shared/tag/TagComponent.vue";
import Title from "./shared/title/TitleComponent.vue";
import Paragraph from "./shared/paragraph/ParagraphComponent.vue";
import Buttons from "./shared/buttons/ButtonsComponent.vue";
import SecondaryButton from "./shared/buttons/SecondaryButton.vue";
import Background from "./shared/background/BackgroundComponent.vue";
import type { WithAppScreenshotProps } from "./hero.interfaces";
import DetailComponent from "./shared/details/DetailComponent.vue";
import DetailsComponent from "./shared/details/DetailsComponent.vue";
import PrimaryButton from "./shared/buttons/PrimaryButton.vue";
import EditableElement from "@/views/page/EditableElement.vue";
import type { PageBlock } from "@/views/blocks/PageBlock.interface";
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import SocialProofComponent from "./shared/socialproof/SocialProofComopnent.vue";

const props = defineProps<{
    block: PageBlock<WithAppScreenshotProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <div :id="block.webId">
        <Background :backgroundColor="styleValues.background" :primaryColor="styleValues.brand">
            <div class="flex flex-col items-center justify-between pt-24 sm:pt-32">
                <div></div>
                <div
                    class="flex flex-col items-center justify-center gap-y-8 max-w-3xl p-6 text-center"
                >
                    <EditableElement
                        v-if="!dataValues.tag.isHidden"
                        :blockId="block.id"
                        field-name="tag"
                    >
                        <Tag
                            v-bind="dataValues.tag.data"
                            :buttonTextColor="styleValues.brand"
                            :textColor="styleValues.text"
                            :borderColor="styleValues.border"
                        />
                    </EditableElement>
                    <SocialProofComponent
                        :blockId="block.id"
                        :socialProofText="dataValues.socialProofText"
                        :socialProofImages="dataValues.socialProofImages.data"
                        :borderColor="styleValues.background"
                        :textColor="styleValues.text"
                    />
                    <EditableElement
                        v-if="!dataValues.title.isHidden"
                        :blockId="block.id"
                        field-name="title"
                    >
                        <Title v-bind="dataValues.title.data" :textColor="styleValues.text" />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.paragraph.isHidden"
                        :blockId="block.id"
                        field-name="paragraph"
                    >
                        <Paragraph
                            v-bind="dataValues.paragraph.data"
                            :textColor="styleValues.text"
                        />
                    </EditableElement>
                    <DetailsComponent
                        v-if="!dataValues.details.isHidden"
                        :textColor="styleValues.text"
                    >
                        <EditableElement
                            v-for="(detail, index) in dataValues.details.data.items"
                            :key="detail.id"
                            :blockId="block.id"
                            :field-name="`details_${detail.id}`"
                            :hideable="false"
                        >
                            <DetailComponent
                                :title="detail.title"
                                :description="detail.description"
                                :is-last="index === dataValues.details.data.items.length - 1"
                                :textColor="styleValues.text"
                            />
                        </EditableElement>
                    </DetailsComponent>
                    <Buttons>
                        <EditableElement
                            v-if="!dataValues.primaryButton.isHidden"
                            :blockId="block.id"
                            field-name="primaryButton"
                        >
                            <PrimaryButton
                                v-bind="dataValues.primaryButton.data"
                                :backgroundColor="styleValues.brand"
                                :textColor="styleValues.background"
                            />
                        </EditableElement>
                        <EditableElement
                            v-if="!dataValues.secondaryButton.isHidden"
                            :blockId="block.id"
                            field-name="secondaryButton"
                        >
                            <SecondaryButton
                                v-bind="dataValues.secondaryButton.data"
                                :textColor="styleValues.text"
                                :borderColor="styleValues.border"
                            />
                        </EditableElement>
                    </Buttons>
                </div>
                <img
                    v-if="!dataValues.image.isHidden"
                    :src="dataValues.image?.data.src"
                    :alt="dataValues.image?.data.alt"
                    class="object-cover"
                />
            </div>
        </Background>
    </div>
</template>
